import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { YaksonAiPanel } from '@/components/yakson-ai/YaksonAiPanel';
import { bindChatToUser, refreshUnread, useChat } from '@/components/yakson-ai/chatStore';
import { useAuth } from '@/lib/auth/auth-context';
import { UserMenu } from './UserMenu';
import { firstTarget, type NavNode } from './navTree';
import type { NavState } from './useNavTree';
import { useLocalePrefix } from '@/lib/hooks/useLocaleNavigate';
import { cn } from '@/lib/utils';

interface IconRailProps {
  nav: NavState;
}

/**
 * GEN-04-02 (Angela, 22 Oct 2026, design N1) — the dark icon rail down the left
 * edge: the Yakson symbol (→ Dashboard, the sign-in's own first view, DSH-07-04),
 * then the top menus as icon + short name, the open one a pink pill; at the
 * bottom Yakson AI and the user's initial (profile menu). The open menu's pages
 * are in the white panel beside it (Sidebar, GEN-04-04). No search box.
 */
export function IconRail({ nav }: IconRailProps) {
  const { t } = useTranslation('nav');
  const prefix = useLocalePrefix();

  const menuHref = (node: NavNode) => prefix + (firstTarget(node)?.href ?? '/dashboard');

  return (
    <header
      className="flex w-20 flex-shrink-0 flex-col items-center gap-1 bg-[#1E1B2E] py-4"
      data-testid="app-header"
    >
      <Link
        to={prefix + '/dashboard'}
        className="mb-3 flex h-[44px] w-[44px] items-center justify-center"
        aria-label={t('dashboard')}
        data-testid="header-logo"
      >
        {/* Angela, 22 Oct 2026: the Yakson symbol, not a letter. */}
        <img src="/yakson-icon.png" alt="Yakson" className="h-full w-full object-contain" />
      </Link>

      <nav
        className="flex min-h-0 flex-1 flex-col items-center gap-1 overflow-y-auto"
        aria-label="Main menu"
        data-testid="top-menus"
      >
        {nav.topMenus.map((node) => {
          const active = nav.activeTop?.id === node.id;
          const Icon = node.icon;
          return (
            <Link
              key={node.id}
              to={menuHref(node)}
              data-testid={`nav-${node.id}`}
              data-active={active || undefined}
              aria-current={active ? 'page' : undefined}
              title={t(node.labelKey)}
              className={cn(
                'flex w-[70px] flex-col items-center gap-1 rounded-[13px] px-0.5 py-2 text-center text-[10.5px] leading-tight transition-colors',
                active
                  ? 'bg-[#FF8189] font-bold text-white'
                  : 'text-[#C9C4DA] hover:bg-white/10 hover:text-white',
              )}
            >
              {Icon && <Icon className="h-5 w-5" aria-hidden="true" />}
              <span className="w-full break-words">{t(node.labelKey)}</span>
            </Link>
          );
        })}
      </nav>

      <div className="mt-2 flex flex-col items-center gap-3">
        <YaksonAiEntry />
        <UserMenu placement="rail" />
      </div>
    </header>
  );
}

/**
 * GEN-12-02: Yakson AI opens its panel from the right. A small red number on the
 * icon = HQ answers to her own questions that she has not opened yet.
 */
function YaksonAiEntry() {
  const { t } = useTranslation('nav');
  const { user } = useAuth();
  const { unread } = useChat();
  const [open, setOpen] = useState(false);
  const userId = user?.id ?? null;
  useEffect(() => {
    bindChatToUser(userId);
    if (!userId) return;
    void refreshUnread();
    const timer = window.setInterval(() => void refreshUnread(), 60_000);
    return () => window.clearInterval(timer);
  }, [userId]);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-[70px] flex-col items-center gap-1 rounded-[13px] px-0.5 py-2 text-[10.5px] leading-tight text-[#C9C4DA] transition-colors hover:bg-white/10 hover:text-white"
        data-testid="header-yakson-ai"
        title={t('yaksonAi')}
      >
        <span className="relative">
          <Sparkles className="h-5 w-5" aria-hidden="true" />
          {unread > 0 && (
            <span
              className="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white"
              role="status"
              aria-label={t('yaksonAiUnread', { count: unread })}
              data-testid="yakson-ai-unread-badge"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </span>
        <span className="w-full break-words">{t('yaksonAi')}</span>
      </button>
      {open && <YaksonAiPanel onClose={() => setOpen(false)} />}
    </>
  );
}
